import { useTranslation } from 'react-i18next';

import BehaviorFish from '@/assets/icons/behavior_fish.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { useChartTheme } from '@/components/charts/theme';
import { Disclosure } from '@/components/molecules/Disclosure';
import type { HealthOverviewParameter, HealthScoreMetrics } from '@/domain/health';
import { toHealthPercent } from '@/routes/Client/Cage/pages/Health/_utils/healthDetailOverviewHelpers';
import {
    laksvelScorePercents,
    ParameterBar,
} from '@/routes/Client/Cage/pages/Health/components/ParameterBar';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import { HEALTH_DEVELOPMENT_CHART_COLORS } from '../../healthChartColors';
import { getParameterFishCount } from '../../healthOverviewChartMappers';
import LaksvelCompactSummaryCard from '../LaksvelCompactSummaryCard/LaksvelCompactSummaryCard';

import { ALERT_LEVEL_COLORS } from './laksvelOverviewColors';

import styles from './LaksvelOverviewChart.module.scss';

/** A day with no scores must not become a full score-0 bar. Zero is a real measurement. */
function measuredScorePercents(scores: HealthScoreMetrics | null) {
    const measured = [scores?.score0, scores?.score1, scores?.score2, scores?.score3].some(
        (score) => score != null
    );
    if (!measured || scores == null) {
        return null;
    }

    return laksvelScorePercents(
        toHealthPercent(scores.score1) ?? 0,
        toHealthPercent(scores.score2) ?? 0,
        toHealthPercent(scores.score3) ?? 0
    );
}

interface ParameterRowProps {
    parameter: HealthOverviewParameter;
    label: string;
    expanded: boolean;
    isLoading: boolean;
    onToggle: () => void;
    onNavigate: (() => void) | null;
}

export default function ParameterRow(props: ParameterRowProps) {
    const { parameter, label, expanded, isLoading, onToggle, onNavigate } = props;
    const { t, i18n } = useTranslation('healthModule');
    const locale = parseLocale(i18n.language);

    const scores = parameter.daily ?? parameter.weekly;
    const percents = measuredScorePercents(scores);

    const chartTheme = useChartTheme();
    const indicatorColor = chartTheme.color(
        ALERT_LEVEL_COLORS[parameter.alert.level] ?? HEALTH_DEVELOPMENT_CHART_COLORS.green
    );

    // `daily` is this range's end day. A missing count must not fall back to the latest week's rollup.
    const fishCount =
        parameter.daily != null ? parameter.daily.count : getParameterFishCount(parameter);
    const fishCountLabel =
        fishCount != null
            ? formatNumberByLocale(fishCount, locale, 0)
            : t('dashboard.notAvailable');

    const fishCountDisplay = (
        <span className={styles.rowFishCount}>
            {fishCount != null ? <SvgIcon icon={BehaviorFish} size="small" /> : null}
            <span className={styles.rowFishCountValue}>{fishCountLabel}</span>
        </span>
    );

    return (
        <Disclosure
            size="compact"
            radius="sm"
            spacing="none"
            animated
            open={expanded}
            onOpenChange={onToggle}
            className={styles.card}
            title={
                <span className={styles.headerContent}>
                    <span className={styles.headerLeft}>
                        <span
                            className={styles.indicator}
                            style={{ backgroundColor: indicatorColor }}
                        />
                        <span className={styles.label}>{label}</span>
                    </span>
                    <ParameterBar percents={percents} height="md" className={styles.bar} />
                </span>
            }
            headerEnd={
                fishCount != null ? (
                    <div
                        className={styles.fishCountTooltip}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}>
                        <Tooltip
                            placement="bottom"
                            maxWidth={280}
                            content={t('dashboard.parameterFishCountTooltip')}
                            layout="inline">
                            {fishCountDisplay}
                        </Tooltip>
                    </div>
                ) : (
                    fishCountDisplay
                )
            }>
            <LaksvelCompactSummaryCard
                parameter={parameter}
                expanded={expanded}
                isLoading={isLoading}
                fishCountLabel={fishCountLabel}
                onNavigate={onNavigate}
            />
        </Disclosure>
    );
}
